import { FC } from 'react';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import {
	GridColumnMenu,
	GridColumnMenuItemProps,
	GridSlotProps
} from '@mui/x-data-grid';
import PushpinOutlined from '@ant-design/icons/PushpinOutlined';
import PushpinFilled from '@ant-design/icons/PushpinFilled';

// ==============================|| GLOBAL TABLE - COLUMN PINNING (left / right) ||============================== //
// Column pinning is a Pro feature in MUI X, but the rendering pipeline also exists in
// the MIT build: pinned columns are moved to the edges of the column list and the
// grid's internal `pinnedColumns` state is written directly from CustomTable (see
// component.tsx) — sticky positioning and offsets are therefore handled natively, in
// sync with the scroll. This file holds the per-column menu commands.

export type PinnedSide = 'left' | 'right';

export interface PinnedColumns {
	left: string[]; // in display order, from the left edge
	right: string[]; // in display order, the last entry touches the right edge
}

// Extra props received by the column menu through slotProps.columnMenu.
declare module '@mui/x-data-grid' {
	interface ColumnMenuPropsOverrides {
		pinnedColumns: PinnedColumns;
		onPinColumn: (field: string, side: PinnedSide) => void;
		onUnpinColumn: (field: string) => void;
	}
}

// Pinning items in the column menu (after "Sort", before "Manage columns").
const PinColumnMenuItems: FC<GridColumnMenuItemProps> = (props) => {
	const { colDef, onClick, pinnedColumns, onPinColumn, onUnpinColumn } = props;
	const isLeft = pinnedColumns.left.includes(colDef.field);
	const isRight = pinnedColumns.right.includes(colDef.field);

	return (
		<>
			{!isLeft && (
				<MenuItem
					onClick={(event) => {
						onPinColumn(colDef.field, 'left');
						onClick(event);
					}}
				>
					<ListItemIcon>
						<PushpinOutlined />
					</ListItemIcon>
					<ListItemText>Fixează la stânga</ListItemText>
				</MenuItem>
			)}
			{!isRight && (
				<MenuItem
					onClick={(event) => {
						onPinColumn(colDef.field, 'right');
						onClick(event);
					}}
				>
					<ListItemIcon>
						<PushpinOutlined style={{ transform: 'scaleX(-1)' }} />
					</ListItemIcon>
					<ListItemText>Fixează la dreapta</ListItemText>
				</MenuItem>
			)}
			{(isLeft || isRight) && (
				<MenuItem
					onClick={(event) => {
						onUnpinColumn(colDef.field);
						onClick(event);
					}}
				>
					<ListItemIcon>
						<PushpinFilled />
					</ListItemIcon>
					<ListItemText>Anulează fixarea</ListItemText>
				</MenuItem>
			)}
		</>
	);
};

// The standard column menu + the pinning items (all other items stay default).
export const ColumnMenuWithPinning: FC<GridSlotProps['columnMenu']> = ({
	pinnedColumns,
	onPinColumn,
	onUnpinColumn,
	...menuProps
}) => (
	<GridColumnMenu
		{...menuProps}
		slots={{ columnMenuPinningItem: PinColumnMenuItems }}
		slotProps={{
			columnMenuPinningItem: {
				displayOrder: 15,
				pinnedColumns,
				onPinColumn,
				onUnpinColumn
			}
		}}
	/>
);
